/**
 * LAB-4507 — pure OpenAPI → endpoint-rows parser.
 *
 * Security boundary (see architecture §5): the OpenAPI spec is
 * customer/attacker-influenced content. This util:
 *   1. enforces a byte-size guard BEFORE parsing (DoS protection),
 *   2. caps the number of produced rows (pathological path counts),
 *   3. returns raw strings UNMODIFIED as data — escaping is the renderer's job.
 *
 * It never touches the DOM and returns data only.
 */

import yaml from 'js-yaml';

import type {
  AccessClassification,
  EndpointParameter,
  EndpointRow,
  EndpointsSummary,
} from '@/sections/asset/types/endpoints.types';

/** Reject specs larger than this (bytes) before parsing. ~2 MB. */
export const MAX_SPEC_BYTES = 2_000_000;

/** Cap produced rows to bound memory/render cost. */
export const MAX_ENDPOINT_ROWS = 2000;

export type EndpointsParseError = 'empty' | 'parse' | 'too-large';

export interface OpenapiToEndpointsResult {
  endpoints: EndpointRow[];
  summary: EndpointsSummary;
  error?: EndpointsParseError;
}

/** HTTP verbs recognized as operations on a path item. */
const HTTP_METHODS = [
  'get',
  'post',
  'put',
  'patch',
  'delete',
  'head',
  'options',
  'trace',
] as const;

const EMPTY_SUMMARY: EndpointsSummary = {
  endpointCount: 0,
  methodCount: 0,
  unauthenticated: null,
  lastCaptured: null,
};

const fail = (error: EndpointsParseError): OpenapiToEndpointsResult => ({
  endpoints: [],
  summary: EMPTY_SUMMARY,
  error,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toParameter = (raw: unknown): EndpointParameter | null => {
  if (!isRecord(raw)) return null;
  const name = typeof raw.name === 'string' ? raw.name : '';
  if (!name) return null;

  const location = typeof raw.in === 'string' ? raw.in : 'query';
  const schema = isRecord(raw.schema) ? raw.schema : undefined;
  const type =
    schema && typeof schema.type === 'string'
      ? schema.type
      : typeof raw.type === 'string'
        ? raw.type
        : 'string';

  return {
    name,
    in: location,
    type,
    required: raw.required === true,
  };
};

const collectParameters = (raw: unknown): EndpointParameter[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map(toParameter).filter((p): p is EndpointParameter => p !== null);
};

/** Response codes that indicate authorization is enforced on the endpoint. */
const AUTH_REQUIRED_CODES = new Set([401, 403]);

/** Distinct numeric response codes from an OpenAPI `responses` object, ascending. */
const collectResponseCodes = (responsesRaw: unknown): number[] => {
  if (!isRecord(responsesRaw)) return [];
  const codes = Object.keys(responsesRaw)
    .map(key => Number.parseInt(key, 10))
    .filter(code => Number.isFinite(code));
  return Array.from(new Set(codes)).sort((a, b) => a - b);
};

/**
 * Classify an endpoint's access from its observed response codes.
 * Precedence: a 401/403 (auth enforced) wins over a 2xx (reached without an
 * auth rejection); anything else (only 404/other-4xx, 5xx, 3xx, or none) is
 * inconclusive.
 */
const classifyAccess = (codes: number[]): AccessClassification => {
  if (codes.some(code => AUTH_REQUIRED_CODES.has(code))) return 'auth-required';
  if (codes.some(code => code >= 200 && code < 300)) return 'no-auth';
  return 'inconclusive';
};

/**
 * Parse an OpenAPI YAML string into endpoint rows + a summary.
 * Enforces the size guard first, then `yaml.load()`, then walks `paths`.
 */
export function openapiToEndpoints(spec: string): OpenapiToEndpointsResult {
  if (!spec || !spec.trim()) {
    return fail('empty');
  }

  // Size guard BEFORE parsing (DoS protection).
  if (spec.length > MAX_SPEC_BYTES) {
    return fail('too-large');
  }

  let parsed: unknown;
  try {
    parsed = yaml.load(spec);
  } catch {
    return fail('parse');
  }

  if (!isRecord(parsed) || !isRecord(parsed.paths)) {
    return fail('parse');
  }

  const paths = parsed.paths;
  const rows: EndpointRow[] = [];
  const methods = new Set<string>();

  outer: for (const [path, pathItemRaw] of Object.entries(paths)) {
    if (!isRecord(pathItemRaw)) continue;

    const pathLevelParams = collectParameters(pathItemRaw.parameters);

    for (const verb of HTTP_METHODS) {
      const operationRaw = pathItemRaw[verb];
      if (!isRecord(operationRaw)) continue;

      if (rows.length >= MAX_ENDPOINT_ROWS) break outer;

      const method = verb.toUpperCase();
      const parameters = [
        ...pathLevelParams,
        ...collectParameters(operationRaw.parameters),
      ];

      const responseCodes = collectResponseCodes(operationRaw.responses);

      methods.add(method);
      rows.push({
        id: `${method} ${path}`,
        method,
        path,
        parameterCount: parameters.length,
        parameters,
        access: classifyAccess(responseCodes),
        responseCodes,
      });
    }
  }

  return {
    endpoints: rows,
    summary: {
      endpointCount: rows.length,
      methodCount: methods.size,
      // Count of endpoints reachable without auth (a 2xx, no 401/403 observed).
      unauthenticated: rows.filter(r => r.access === 'no-auth').length,
      lastCaptured: null,
    },
  };
}
